/**
 * Shared Arabic vocabulary for GUEST RELOCATION — "the guest is not in the unit their booking names".
 *
 * Three surfaces will render the one backend answer (`GET /api/v1/relocations/current`):
 *   - BookingDetails.js        — the banner above everything else on the page
 *   - the arrivals/readiness view
 *   - the inbox thread header
 *
 * The backend half of "they must never disagree" is structural — one endpoint, one answer. THIS
 * FILE IS THE OTHER HALF. Agreeing on the data while retyping the words is the worse failure of the
 * two: it is invisible in a diff, and the words are what an operator acts on at 2am.
 *
 * Same pattern and same reasoning as `config/arrivalLabels.js` and `config/guestRequestLabels.js`.
 *
 * ⚠️ ARABIC-ONLY, deliberately — this is ops territory. The booking page hardcodes its Arabic, the
 * fetch is gated on core staff, and the crew (who English exists for, per I18N_STRATEGY) can never
 * reach this surface. Putting these words in the bilingual locale files would imply a translated
 * surface that does not exist, and would render an English LTR island inside a hardcoded-RTL page
 * for any staff account carrying `locale: 'en'`. If relocation ever joins the translated surface,
 * these constants become t() calls in each consumer — the import boundary is what makes that small.
 */

export const RELOCATION_BOOKED = 'محجوز:';
export const RELOCATION_CURRENTLY_IN = 'الضيف الآن في:';
/**
 * The same banner for a move that has NOT STARTED yet — recorded before the guest arrived.
 *
 * ⚠️ A separate sentence, not a reuse. «الضيف الآن في» about a move that begins on Thursday is a
 * false statement about tonight, and this banner's entire job is telling an operator which door to
 * go to right now.
 */
export const RELOCATION_WILL_MOVE_TO = 'سينتقل الضيف إلى:';
export const RELOCATION_PERIOD = 'الفترة:';

/**
 * Outstanding work, keyed so a consumer never assembles its own sentence.
 *
 * ⚠️ The old-code line is a SECURITY fact, not a status. «لم يتم تأكيد» — "not confirmed" — is the
 * honest claim: the vendor accepted our revoke request and we could not prove the code is gone.
 * Never «تم الإلغاء» here, and never a phrasing that reads as "failed", because a failure and an
 * unconfirmed success need different responses from the person reading it.
 */
export const RELOCATION_OUTSTANDING_AR = {
  oldCodeUnverified: 'لم يتم تأكيد إلغاء رمز الوحدة السابقة',
  // ⚠️ Not the same claim, and it used to be treated as no claim at all. A deferred revocation only
  // survives when the guest is still IN the original unit on a move dated later — and nothing in V1
  // comes back on the day to do it. The code is definitely live, and a person has to kill it.
  oldCodeDeferred: 'رمز الوحدة السابقة ما زال فعّالاً — يلزم إلغاؤه يدوياً يوم النقل',
  // ⚠️ Deliberately NOT «لم يتم تأكيد»: that sentence says we asked and could not confirm. Here no
  // request was ever made — the credential was attached to another unit — so nothing is known.
  oldCodeNotAttempted: 'لم تُنفَّذ عملية إلغاء رمز الوحدة السابقة — تحقق يدوياً',
  // (The manual-Gathern line was removed 2026-08-08 — the export feed carries the block; a block
  // typed into the extranet is one nothing ever unwinds.)
};

/**
 * The old code, by what we actually know about it. Keys are `oldCode.status` on the allocation.
 *
 * ⚠️ Three different facts, three different sentences — this is the distinction the whole L7 design
 * turns on. «لم يتم تأكيد» is an unconfirmed success (the vendor took our request, we could not
 * prove the code is gone). «فشل» is a failure we watched happen. Collapsing them into one line
 * would have ops treat a real failure as paperwork, or chase a code that is already dead.
 */
export const RELOCATION_OLD_CODE_AR = {
  requested: 'طُلب إلغاء رمز الوحدة السابقة — لم يتأكد بعد',
  acknowledged: 'لم يتم تأكيد إلغاء رمز الوحدة السابقة',
  verified: 'تم تأكيد إلغاء رمز الوحدة السابقة',
  failed: 'فشل إلغاء رمز الوحدة السابقة — الرمز قد يكون فعّالاً',
  // ⚠️ Says the code is STILL LIVE, not that something will handle it. It used to read
  // «سيُلغى … عند بدء النقل» — a promise nothing kept, because no job re-processes a deferral.
  deferred: 'رمز الوحدة السابقة ما زال فعّالاً — يلزم إلغاؤه يدوياً يوم النقل',
  // ⚠️ The one outcome where we never asked the vendor anything: the booking's credential was
  // already attached to another unit, so the origin door was left untouched. Says "check it
  // yourself", because unlike «لم يتم تأكيد» there is not even a request to point at.
  already_moved: 'لم تُنفَّذ عملية إلغاء رمز الوحدة السابقة — تحقق يدوياً',
  not_applicable: 'لا يوجد رمز على الوحدة السابقة'
};

/**
 * We could not find out whether this guest has been moved.
 *
 * ⚠️ Shown INSTEAD of silence. An empty space where the banner would be reads as "the guest is in
 * the unit on their booking" — the one wrong answer this whole surface exists to prevent — and the
 * operator has no way to tell the two apart. Same reasoning as `ARRIVAL_ERROR_AR`.
 */
export const RELOCATION_UNKNOWN_AR = 'تعذّر التحقق من موقع الضيف الحالي — لا تعتمد على الوحدة الظاهرة أدناه.';

// ── The move itself ──────────────────────────────────────────────────────────
export const RELOCATION_ACTION = 'نقل الضيف إلى وحدة أخرى';
export const RELOCATION_MODAL_TITLE = 'نقل الضيف';

/**
 * Why the guest is being moved. Keys are the model's `reason` enum.
 *
 * The operator picks from a list rather than typing, because this is the field a future report
 * groups by — "how often is a unit not ready at the door" is a question worth being able to ask.
 */
export const RELOCATION_REASON_LABELS = {
  not_ready: 'الوحدة غير جاهزة',
  fault: 'عطل في الوحدة',
  lock_problem: 'مشكلة في القفل',
  overbooking: 'حجز مزدوج',
  guest_request: 'طلب الضيف',
  other: 'سبب آخر'
};

/**
 * Why a unit is NOT offered. Keys are the backend's `ineligible[].reason`.
 *
 * ⚠️ These are shown, never hidden. At 2am the difference between "someone bought it" and "its
 * lock is dead" sends the operator somewhere completely different, and a list that silently omits
 * half the estate teaches people not to trust it.
 */
export const RELOCATION_INELIGIBLE_LABELS = {
  sold: 'محجوزة خلال هذه الفترة',
  occupied: 'يوجد بها ضيف تم نقله إليها',
  unavailable: 'مغلقة خلال هذه الفترة',
  lock_unreachable: 'القفل لا يستجيب حالياً',
  not_active: 'الوحدة غير مفعّلة'
};

/**
 * Why there is no list at all. Keys are the backend's `notEligible`.
 *
 * ⚠️ Distinct from an empty list on purpose. «لا توجد وحدة متاحة» sends an operator hunting for a
 * free room; neither of these is about rooms, and the move would be refused anyway.
 */
export const RELOCATION_NOT_ELIGIBLE_LABELS = {
  booking_cancelled: 'هذا الحجز ملغي — لا يوجد ضيف لنقله',
  stay_over: 'انتهت إقامة هذا الضيف — لا يوجد أحد لنقله'
};

/**
 * The steps of a move, in order. Keys are the model's `state` enum.
 *
 * ⚠️ Each one describes WHAT IS TRUE, never an instruction. «تم إصدار رمز جديد» — the code has been
 * issued — not «أصدر رمزاً». The operator is recording what happened, and a button that reads like
 * an order invites a press before the thing is done.
 */
export const RELOCATION_STATE_LABELS = {
  held: 'تم حجز الوحدة المستقبِلة',
  code_issued: 'تم إصدار رمز جديد',
  guest_informed: 'تم إبلاغ الضيف',
  moved: 'انتقل الضيف فعلياً',
  old_code_revoked: 'تم إلغاء الرمز القديم',
  complete: 'اكتمل النقل',
  cancelled: 'أُلغي النقل'
};

/**
 * The code rung, in the words that are actually true for the door in front of the operator (D-R3).
 *
 * ⚠️ The rung used to read «تم إصدار رمز جديد» in every case, including two where nothing had been
 * attempted: a receiving unit with no keypad, and a move dated beyond the lock worker's horizon.
 * A tick is a record, and a record of something that did not happen is worse than no record —
 * it is what the next person believes.
 *
 * `live` keeps the original sentence. `manual` names the thing ops genuinely did (R5's key
 * handover — no code exists and none is coming, so pressing it is honest). `pending` is not a
 * label for a button at all: it is a fact stated while the rung waits.
 */
export const RELOCATION_CODE_RUNG = {
  live: RELOCATION_STATE_LABELS.code_issued,
  manual: 'تم تسليم المفتاح للضيف يدوياً',
  pending: 'بانتظار إصدار الرمز على الوحدة المستقبِلة'
};

/**
 * Why the rung is waiting. Shown under it, so «بانتظار» is never a dead end the operator has to
 * guess at — the two causes need completely different responses (wait a minute vs. wait until the
 * move's start date).
 */
export const RELOCATION_CODE_PENDING_AR = {
  deferred: 'يبدأ النقل لاحقاً — لن يصدر الرمز قبل موعده',
  queued: 'طُلب الرمز ولم يظهر على القفل بعد'
};

/**
 * What the operator should know BEFORE they choose a unit — the two facts that make a move
 * expensive, said once, at the top of the modal.
 *
 * ⚠️ Not a confirm dialog. A dialog trains people to dismiss it, and this is not a "are you sure"
 * question — the operator is standing in a corridor and has already decided. It is a standing
 * notice: read it the first few times, recognise its shape thereafter.
 *
 * The second line is the one that surprises people. Under L9/O1 the SOLD unit keeps the booking and
 * stays closed for the whole stay — it is refused as `existing_booking`, the one refusal admin
 * force-booking cannot override — while the receiving unit is closed too. So a move takes two
 * units' nights out of sale, not one, and the only ways back are to move the guest into it again or
 * to sell it from their checkout onward. Ops improvised around exactly this in production once
 * (§5b of the design record), which is why it is stated rather than left to be discovered.
 */
export const RELOCATION_CAUTION = {
  title: 'النقل إجراء استثنائي',
  when: 'يُستخدم عندما يتعذّر إسكان الضيف في وحدته — عطل، وحدة غير جاهزة، أو مشكلة في القفل. ليس إجراءً روتينياً.',
  cost: 'الوحدة المحجوزة تبقى مغلقة طوال فترة الحجز لأن الحجز ما زال عليها، وتُغلق الوحدة المستقبِلة أيضاً — أي أن ليالي وحدتين تخرج من البيع.'
};

export const RELOCATION_UI = {
  chooseUnit: 'اختر الوحدة المستقبِلة',
  reason: 'سبب النقل',
  notes: 'ملاحظات (اختياري)',
  sameBuilding: 'نفس المبنى',
  // ⚠️ INFORMATION, not an instruction — it stopped being a chore on 2026-08-08. Gathern pulls our
  // calendar on its own ~2h cycle, so the block reaches it late; the operator is told which unit is
  // the slower one and decides for themselves whether tonight's risk is worth closing by hand.
  // Nothing is gated on it, and nobody is asked to unwind anything afterwards.
  manualChannelBlock: 'الإغلاق يصل إلى Gathern خلال ~ساعتين',
  // ⚠️ The loudest thing on the row, and deliberately so (R5, refined). This unit is not on smart
  // locks, so NO code will be issued and the guest gets in only if someone hands them a key. It is
  // not a failure — nothing broke — which is why it stays in the attention family rather than
  // borrowing the red the lock-danger banner owns.
  manualKeyHandover: 'لا يوجد قفل — تسليم يدوي للمفتاح',
  noCandidates: 'لا توجد وحدة متاحة لكامل الفترة المتبقية',
  ruledOut: 'وحدات غير متاحة',
  confirm: 'تسجيل النقل',
  cancelMove: 'إلغاء النقل',
  cancelReason: 'سبب الإلغاء',
  steps: 'خطوات النقل',
  // ⚠️ Said ONCE on the panel rather than as a confirm dialog per press. A modal on every step makes
  // the panel unusable by someone holding a phone in a corridor, which is the only place it is ever
  // opened; a standing sentence at the top does the same job without standing in the way.
  noUndo: 'الخطوات لا يمكن التراجع عنها — سجّل الخطوة بعد تنفيذها.',
  moveSummary: 'من',
  moveSummaryTo: 'إلى',
  // The row already NAMES the step as a completed fact («تم إبلاغ الضيف»). The button next to it is
  // the act of writing that down, so it says so — repeating the phrase on the button would read as
  // an instruction to go and do it.
  record: 'تسجيل',
  confirmShort: 'تأكيد',
  loadFailed: 'تعذّر تحميل خطوات النقل',
  // ⚠️ The action SUCCEEDED and only the re-read failed. Saying "could not do that" here would be a
  // lie that invites a second press — and the second press is refused, so the operator ends up
  // believing the first one failed too.
  refreshFailed: 'تم تنفيذ الإجراء، لكن تعذّر تحديث الحالة — أعد تحميل الصفحة.',
  // The lock's own answer, kept visibly separate from the operator's tick above it. Same rule the
  // arrival/checkout card follows: each row names its source so neither reads as evidence for the
  // other.
  oldCodeSource: 'حسب سجل القفل',
  cancelConfirm: 'تأكيد الإلغاء',
  cancelBack: 'تراجع',
  // The consequences, stated where the button is. Cancelling is not "closing the screen".
  cancelWarning: 'سيُعاد فتح الوحدة المستقبِلة للبيع، ويُلغى الرمز الجديد ويعود رمز الوحدة الأصلية.',
  cancelBlocked: 'انتقل الضيف فعلياً — لا يمكن إلغاء النقل. أعِد الضيف بنقل جديد.',
  // ⚠️ The button that sentence was pointing at, which did not exist. Once the guest has physically
  // moved, a NEW move is the only way anywhere — back to their own unit (the move-back O1 depends
  // on) or onward to a third if the receiving unit fails too. The page's own «نقل الضيف» is hidden
  // while a move exists, so without this the panel was naming a route ops could not take.
  relocateAgain: 'نقل الضيف مرة أخرى',
  done: 'اكتمل النقل',
  // Carries its own colon, like RELOCATION_PERIOD above — punctuation belongs with the word, not
  // assembled in whichever component happens to render it.
  period: 'الفترة المتبقية:',
  // The operator is recording a decision they have already made in the corridor — this is the one
  // place that says so, because the system executes and records but never decides (L14).
  hint: 'اختر الوحدة التي انتقل إليها الضيف. النظام يسجّل النقل ويغلق الوحدة ويصدر رمزاً جديداً.'
};
